if (!customElements.get('bundle-builder')) {
  function waitForElement(selector, parent = document, timeout = 5000) {
    return new Promise((resolve, reject) => {
      const root = parent || document;
      const existing = root.querySelector(selector);
      if (existing) {
        resolve(existing);
        return;
      }

      const start = Date.now();
      const timer = window.setInterval(() => {
        const element = root.querySelector(selector);
        if (element) {
          window.clearInterval(timer);
          resolve(element);
          return;
        }

        if (Date.now() - start >= timeout) {
          window.clearInterval(timer);
          reject(new Error(`waitForElement: ${selector} not found`));
        }
      }, 50);
    });
  }

  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.state = {};
      this.config = {};
      this.activeProduct = null;
      this.activePopupProduct = null;
      this.popupActiveImageIndex = 0;
      this.popupSwiper = null;
      this.pdpSectionsCache = new Map();
      this.pdpPrefetchPromises = new Map();
      this.pdpFetchPromises = new Map();
      this.templateSectionIdsCache = new Map();
      this.templateMainSectionId = new Map();
      this.templateExcludedSectionIds = new Map();
      this.pdpFetchController = null;
      this.pdpPreloadTimer = null;
      this.productStylesLoaded = false;
      this.selectedOptions = {};
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-bundle-config]')?.textContent || '{}');
      this.cacheDom();
      this.bindEvents();
      this.bindPdpPreload();
      this.ensureProductStyles();
      this.render();
    }

    cacheDom() {
      this.dom = {
        grid: this.querySelector('[data-bundle-grid]'),
        sidebarStatus: this.querySelectorAll('[data-sidebar-status]'),
        sidebarProgress: this.querySelector('[data-sidebar-progress]'),
        sidebarSlots: this.querySelector('[data-sidebar-slots]'),
        sidebarItems: this.querySelector('[data-sidebar-items]'),
        sidebarItemCount: this.querySelector('[data-sidebar-item-count]'),
        sidebarSavings: this.querySelector('[data-sidebar-savings]'),
        sidebarSavingsRow: this.querySelector('[data-sidebar-savings-row]'),
        sidebarCompare: this.querySelector('[data-sidebar-compare]'),
        sidebarSubtotal: this.querySelector('[data-sidebar-subtotal]'),
        sidebarFreeItems: this.querySelector('[data-sidebar-free-items]'),
        sidebarCheckout: this.querySelector('[data-sidebar-checkout]'),
        selectedCount: this.querySelector('[data-selected-count]'),
        mobileStatus: this.querySelector('[data-mobile-status]'),
        mobileSlots: this.querySelector('[data-mobile-slots]'),
        mobileBundleStatus: this.querySelector('[data-mobile-bundle-status]'),
        mobileActive: this.querySelector('[data-mobile-active]'),
        mobileThumbs: this.querySelector('[data-mobile-thumbs]'),
        mobileProgress: this.querySelector('[data-mobile-progress]'),
        mobileCount: this.querySelector('[data-mobile-count]'),
        mobilePrice: this.querySelector('[data-mobile-price]'),
        mobileCompare: this.querySelector('[data-mobile-compare]'),
        mobileFreeItems: this.querySelector('[data-mobile-free-items]'),
        mobileCheckout: this.querySelector('[data-mobile-checkout]'),
        modal: this.querySelector('[data-variant-modal]'),
        modalTitle: this.querySelector('[data-modal-title]'),
        modalImage: this.querySelector('[data-modal-image]'),
        modalPrice: this.querySelector('[data-modal-price]'),
        modalOptions: this.querySelector('[data-modal-options]'),
        modalAdd: this.querySelector('[data-modal-add]'),
        error: this.querySelector('[data-bundle-error]'),
        productPopup: this.querySelector('[data-product-popup]'),
        productPopupMainImage: this.querySelector('[data-product-popup-main-image]'),
        productPopupThumbs: this.querySelector('[data-product-popup-thumbs]'),
        productPopupTitle: this.querySelector('[data-product-popup-title]'),
        productPopupRating: this.querySelector('[data-product-popup-rating]'),
        productPopupPrices: this.querySelector('[data-product-popup-prices]'),
        productPopupMobilePrices: this.querySelector('[data-product-popup-mobile-prices]'),
        productPopupCollapsibles: this.querySelector('[data-product-popup-collapsibles]'),
        productPopupBody: this.querySelector('[data-product-popup-body]'),
        productPopupPdpLoading: this.querySelector('[data-product-popup-pdp-loading]'),
        productPopupPdpSections: this.querySelector('[data-product-popup-pdp-sections]'),
        productPopupAdd: this.querySelector('[data-product-popup-add]'),
        productPopupMobileAdd: this.querySelector('[data-product-popup-mobile-add]'),
      };
    }

    bindEvents() {
      this.addEventListener('click', (e) => {
        const openProductBtn = e.target.closest('[data-open-product]');
        if (openProductBtn) {
          if (this.config.enableProductPopup !== false) {
            // Let command/ctrl/shift clicks open in new tab/window naturally
            if (!e.metaKey && !e.ctrlKey && !e.shiftKey && e.button !== 1) {
              e.preventDefault();
              this.handleOpenProductClick(openProductBtn);
              return;
            }
          }
        }

        const thumbBtn = e.target.closest('[data-product-popup-thumb]');
        if (thumbBtn) {
          e.preventDefault();
          this.setPopupImage(Number(thumbBtn.dataset.index || 0));
          return;
        }

        const popupAddBtn = e.target.closest('[data-product-popup-add], [data-product-popup-mobile-add]');
        if (popupAddBtn) {
          e.preventDefault();
          this.handleProductPopupAdd();
          return;
        }

        const addBtn = e.target.closest('[data-add-product]');
        if (addBtn) {
          e.preventDefault();
          this.handleAddClick(addBtn);
          return;
        }

        const incBtn = e.target.closest('[data-increase]');
        if (incBtn) {
          e.preventDefault();
          this.changeQty(incBtn.dataset.productId, 1);
          return;
        }

        const decBtn = e.target.closest('[data-decrease]');
        if (decBtn) {
          e.preventDefault();
          this.changeQty(decBtn.dataset.productId, -1);
          return;
        }

        const removeBtn = e.target.closest('[data-remove-item]');
        if (removeBtn) {
          e.preventDefault();
          this.changeQty(removeBtn.dataset.productId, -1);
          return;
        }

        const removeAllBtn = e.target.closest('[data-remove-all]');
        if (removeAllBtn) {
          e.preventDefault();
          this.removeItem(removeAllBtn.dataset.productId);
          return;
        }

        const checkoutBtn = e.target.closest('[data-checkout]');
        if (checkoutBtn) {
          e.preventDefault();
          this.handleCheckout(checkoutBtn);
          return;
        }

        const closeProductPopup = e.target.closest('[data-close-product-popup]');
        if (closeProductPopup || e.target === this.dom.productPopup) {
          this.closeProductPopup();
          return;
        }

        const closeModal = e.target.closest('[data-close-modal]');
        if (closeModal || e.target === this.dom.modal) {
          this.closeModal();
        }
      });

      this.dom.modalAdd?.addEventListener('click', () => this.confirmVariantAdd());
      document.addEventListener('keydown', this.handleKeydown);
    }

    bindPdpPreload() {
      const schedulePrefetch = (productId) => {
        const product = this.getProducts().find((p) => String(p.id) === String(productId));
        if (!product?.handle) return;
        if (this.pdpSectionsCache.has(product.handle) || this.pdpPrefetchPromises.has(product.handle)) return;

        window.clearTimeout(this.pdpPreloadTimer);
        this.pdpPreloadTimer = window.setTimeout(() => {
          this.prefetchPdpSections(product);
        }, 120);
      };

      this.addEventListener(
        'pointerenter',
        (event) => {
          const card = event.target.closest('[data-product-card]');
          if (card) schedulePrefetch(card.dataset.productId);
        },
        true
      );

      this.addEventListener(
        'touchstart',
        (event) => {
          const card = event.target.closest('[data-product-card]');
          if (!card) return;
          const product = this.getProducts().find((p) => String(p.id) === String(card.dataset.productId));
          if (product) this.prefetchPdpSections(product);
        },
        { passive: true, capture: true }
      );
    }

    getProductById(productId) {
      return this.getProducts().find((p) => String(p.id) === String(productId));
    }

    prefetchPdpSections(product) {
      if (!product?.handle) return;
      if (this.pdpSectionsCache.has(product.handle) || this.pdpPrefetchPromises.has(product.handle)) return;

      const controller = new AbortController();
      const promise = this.fetchPdpContent(product, controller.signal)
        .then((content) => {
          this.pdpSectionsCache.set(product.handle, content);
          return content;
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Bundle builder PDP prefetch error:', error);
          }
          return null;
        })
        .finally(() => {
          this.pdpPrefetchPromises.delete(product.handle);
        });

      this.pdpPrefetchPromises.set(product.handle, promise);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      this.pdpFetchController?.abort();
      this.pdpFetchController = null;
      this.destroyPopupSlider();
      this.unlockBodyScroll();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.dom.productPopup?.classList.contains('is-open')) {
        this.closeProductPopup();
      } else if (this.dom.modal?.classList.contains('is-open')) {
        this.closeModal();
      }
    }

    lockBodyScroll() {
      document.body.style.overflow = 'hidden';
    }

    unlockBodyScroll() {
      const popupOpen = this.dom.productPopup?.classList.contains('is-open');
      const modalOpen = this.dom.modal?.classList.contains('is-open');
      if (!popupOpen && !modalOpen) {
        document.body.style.overflow = '';
      }
    }

    getProducts() {
      return this.config.products || [];
    }

    getRequiredCount() {
      return this.config.requiredCount || 3;
    }

    isAtLimit() {
      return this.getTotals().qty >= this.getRequiredCount();
    }

    getTotals() {
      let qty = 0;
      let price = 0;
      let compare = 0;

      Object.values(this.state).forEach((item) => {
        qty += item.qty;
        price += item.price * item.qty;
        compare += (item.compareAt || item.price) * item.qty;
      });

      return {
        qty,
        price,
        compare,
        savings: Math.max(0, compare - price),
      };
    }

    getUnitPrices() {
      const prices = [];

      Object.values(this.state).forEach((item) => {
        for (let i = 0; i < item.qty; i += 1) {
          prices.push(item.price);
        }
      });

      return prices;
    }

    getDisplayTotals() {
      const totals = this.getTotals();
      const required = this.getRequiredCount();

      if (totals.qty === required) {
        const unitPrices = this.getUnitPrices();
        const highestPrice = Math.max(...unitPrices);
        const sumPrice = unitPrices.reduce((sum, value) => sum + value, 0);

        return {
          ...totals,
          price: highestPrice,
          compare: sumPrice,
          savings: Math.max(0, sumPrice - highestPrice),
        };
      }

      return totals;
    }

    getStatusMessage(qty, required) {
      const remaining = Math.max(0, required - qty);

      if (qty >= required) {
        return '🎉 You unlocked 2 FREE items!';
      }

      if (remaining === required) {
        return 'Add 3 products to unlock B1G2 Offer';
      }

      if (remaining === 2) {
        return 'Add 2 more to unlock B1G2 Offer 🔥';
      }

      if (remaining === 1) {
        return 'Add 1 more to unlock B1G2 Offer';
      }

      return `Add ${remaining} more to unlock B1G2 Offer`;
    }

    getUnitItems() {
      const units = [];

      Object.values(this.state).forEach((item) => {
        for (let i = 0; i < item.qty; i += 1) {
          units.push(item);
        }
      });

      return units;
    }

    isBundleComplete() {
      return this.getTotals().qty >= this.getRequiredCount();
    }

    getProductQty(productId) {
      return this.state[String(productId)]?.qty || 0;
    }

    handleOpenProductClick(btn) {
      const productId = btn.dataset.productId;
      const product = this.getProducts().find((p) => String(p.id) === String(productId));
      if (!product) return;
      this.openProductPopup(product);
    }

    openProductPopup(product) {
      this.activePopupProduct = product;
      this.popupActiveImageIndex = 0;
      this.ensureProductStyles();
      this.renderProductPopup();
      this.dom.productPopup?.classList.add('is-open');
      this.lockBodyScroll();
      this.initProductPopupRating();
      this.loadPdpSections(product);
    }

    closeProductPopup() {
      this.pdpFetchController?.abort();
      this.pdpFetchController = null;
      this.destroyPopupSlider();
      this.clearPdpSections();
      this.dom.productPopup?.classList.remove('is-open');
      this.unlockBodyScroll();
      this.activePopupProduct = null;
      this.popupActiveImageIndex = 0;
    }

    getProductPageUrl(product) {
      const baseUrl = window.MinimogSettings?.base_url || window.Shopify?.routes?.root || '/';
      const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      const productPath = product.url?.startsWith('/') ? product.url.slice(1) : product.url;
      return `${normalizedBase}${productPath}`;
    }

    ensureProductStyles() {
      if (this.productStylesLoaded) return;

      const cssUrl = this.config.productCssUrl;
      if (!cssUrl) return;

      if (!document.querySelector(`link[href="${cssUrl}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        document.head.appendChild(link);
      }

      this.productStylesLoaded = true;
    }

    getMainProductSection(doc) {
      return doc
        .querySelector('#MainContent .shopify-section [data-section-type="product-page"]')
        ?.closest('.shopify-section');
    }

    extractCollapsiblesHtml(doc) {
      const mainSection = this.getMainProductSection(doc);
      if (!mainSection) return '';

      return [...mainSection.querySelectorAll('.m-product-collapsible')]
        .map((element) => element.outerHTML)
        .join('');
    }

    getSectionApiId(sectionElement) {
      return sectionElement.id?.replace(/^shopify-section-/, '') || '';
    }

    getTemplateKey(product) {
      return product.templateSuffix || 'default';
    }

    cacheTemplateManifest(doc, templateKey) {
      const sections = [...doc.querySelectorAll('#MainContent .shopify-section')];
      const mainSection = this.getMainProductSection(doc);
      const mainId = mainSection ? this.getSectionApiId(mainSection) : '';
      const sectionIds = [];
      const excluded = new Set();

      sections.forEach((section) => {
        const id = this.getSectionApiId(section);
        if (!id) return;
        sectionIds.push(id);
        if (this.shouldExcludePdpSection(section)) {
          excluded.add(id);
        }
      });

      this.templateSectionIdsCache.set(templateKey, sectionIds);
      this.templateMainSectionId.set(templateKey, mainId);
      this.templateExcludedSectionIds.set(templateKey, excluded);
    }

    extractCollapsiblesFromHtml(html) {
      if (!html) return '';

      const doc = new DOMParser().parseFromString(html, 'text/html');
      return [...doc.querySelectorAll('.m-product-collapsible')]
        .map((element) => element.outerHTML)
        .join('');
    }

    async fetchRenderedSectionsData(productUrl, sectionIds, signal) {
      if (!sectionIds.length) return {};

      const chunkSize = 5;
      const chunks = [];

      for (let index = 0; index < sectionIds.length; index += chunkSize) {
        chunks.push(sectionIds.slice(index, index + chunkSize));
      }

      const chunkResults = await Promise.all(
        chunks.map(async (ids) => {
          const url = new URL(productUrl, window.location.origin);
          url.searchParams.set('sections', ids.join(','));

          const response = await fetch(url.toString(), { signal });
          if (!response.ok) {
            throw new Error(`Failed to load rendered sections (${response.status})`);
          }

          return response.json();
        })
      );

      return Object.assign({}, ...chunkResults);
    }

    buildPdpContentFromSectionsData(sectionsData, sectionIds, templateKey) {
      const mainId = this.templateMainSectionId.get(templateKey) || '';
      const excluded = this.templateExcludedSectionIds.get(templateKey) || new Set();
      const collapsiblesHtml = mainId ? this.extractCollapsiblesFromHtml(sectionsData[mainId] || '') : '';
      const sectionsHtml = sectionIds
        .filter((id) => id !== mainId && !excluded.has(id))
        .map((id) => sectionsData[id] || '')
        .filter(Boolean)
        .join('');

      return { collapsiblesHtml, sectionsHtml };
    }

    async fetchPdpContent(product, signal) {
      const productUrl = this.getProductPageUrl(product);
      const templateKey = this.getTemplateKey(product);
      let sectionIds = this.templateSectionIdsCache.get(templateKey);

      if (!sectionIds) {
        const response = await fetch(productUrl, { signal });
        if (!response.ok) {
          throw new Error(`Failed to load product page sections (${response.status})`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.cacheTemplateManifest(doc, templateKey);
        sectionIds = this.templateSectionIdsCache.get(templateKey) || [];

        const earlyCollapsibles = this.extractCollapsiblesHtml(doc);
        if (earlyCollapsibles && this.activePopupProduct?.handle === product.handle) {
          this.renderCollapsibles(earlyCollapsibles);
        }
      }

      const sectionsData = await this.fetchRenderedSectionsData(productUrl, sectionIds, signal);
      return this.buildPdpContentFromSectionsData(sectionsData, sectionIds, templateKey);
    }

    renderCollapsibles(collapsiblesHtml) {
      if (!this.dom.productPopupCollapsibles) return;
      this.dom.productPopupCollapsibles.innerHTML = collapsiblesHtml;
      this.reinitializePdpSections(this.dom.productPopupCollapsibles, { skipGlobalInit: true });
    }

    async loadPdpSections(product) {
      if (!product?.handle) return;

      const cacheKey = product.handle;

      if (this.pdpSectionsCache.has(cacheKey)) {
        this.renderPdpContent(this.pdpSectionsCache.get(cacheKey));
        return;
      }

      const inFlightPrefetch = this.pdpPrefetchPromises.get(cacheKey);
      const inFlightFetch = this.pdpFetchPromises.get(cacheKey);
      const existingPromise = inFlightPrefetch || inFlightFetch;

      if (existingPromise) {
        this.setPdpSectionsLoading(true);
        try {
          const content = await existingPromise;
          if (content && this.activePopupProduct?.handle === product.handle) {
            this.renderPdpContent(content);
          }
        } finally {
          if (this.activePopupProduct?.handle === product.handle) {
            this.setPdpSectionsLoading(false);
          }
        }
        return;
      }

      this.pdpFetchController?.abort();
      this.pdpFetchController = new AbortController();
      this.clearPdpSections();
      this.setPdpSectionsLoading(true);

      const fetchPromise = this.fetchPdpContent(product, this.pdpFetchController.signal)
        .then((content) => {
          this.pdpSectionsCache.set(cacheKey, content);
          return content;
        })
        .finally(() => {
          this.pdpFetchPromises.delete(cacheKey);
        });

      this.pdpFetchPromises.set(cacheKey, fetchPromise);

      try {
        const content = await fetchPromise;
        if (content && this.activePopupProduct?.handle === product.handle) {
          this.renderPdpContent(content);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Bundle builder PDP sections error:', error);
        }
      } finally {
        if (this.activePopupProduct?.handle === product.handle) {
          this.setPdpSectionsLoading(false);
        }
      }
    }

    renderPdpContent({ collapsiblesHtml = '', sectionsHtml = '' } = {}) {
      if (this.dom.productPopupCollapsibles) {
        this.dom.productPopupCollapsibles.innerHTML = collapsiblesHtml;
      }

      if (this.dom.productPopupPdpSections) {
        this.dom.productPopupPdpSections.innerHTML = sectionsHtml;
      }

      this.reinitializePdpSections(this.dom.productPopupCollapsibles, { skipGlobalInit: true });
      this.reinitializePdpSections(this.dom.productPopupPdpSections, { skipGlobalInit: true });
      this.finalizePdpSectionInit();
    }

    finalizePdpSectionInit() {
      const popupBody = this.dom.productPopupBody;
      if (!popupBody) return;

      if (typeof window.jdgm !== 'undefined') {
        if (typeof window.jdgm.customizeBadges === 'function') {
          window.jdgm.customizeBadges();
        }
        if (typeof window.jdgm.renderWidgets === 'function') {
          window.jdgm.renderWidgets(popupBody);
        }
      }

      if (typeof window.SPR !== 'undefined' && typeof window.SPR.initDomEls === 'function') {
        window.SPR.initDomEls();
      }

      waitForElement('.jdgm-widget, .jdgm-review-widget, .shopify-app-block', popupBody, 5000).catch(() => {});

      document.dispatchEvent(
        new CustomEvent('bundle-builder:pdp-sections-loaded', {
          detail: { container: popupBody, product: this.activePopupProduct },
        })
      );
    }

    reinitializePdpSections(container, options = {}) {
      if (!container || !container.innerHTML.trim()) return;

      container.querySelectorAll('script').forEach((script) => {
        const newScript = document.createElement('script');
        [...script.attributes].forEach((attr) => newScript.setAttribute(attr.name, attr.value));
        if (script.textContent) {
          newScript.textContent = script.textContent;
        }
        script.replaceWith(newScript);
      });

      container.querySelectorAll('.shopify-section, .shopify-app-block').forEach((block) => {
        block.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      });

      if (!options.skipGlobalInit) {
        this.finalizePdpSectionInit();
      }
    }

    shouldExcludePdpSection(section) {
      if (!section) return true;
      if (section.querySelector('[data-section-type="product-page"], .m-main-product')) return true;
      if (section.querySelector('[data-section-type="product-details-tabs"], m-product-details-tabs')) return true;
      if (section.querySelector('.m-breadcrumb')) return true;
      if (section.querySelector('bundle-builder, .bundle-builder-section')) return true;
      return false;
    }

    setPdpSectionsLoading(isLoading) {
      if (this.dom.productPopupPdpLoading) {
        this.dom.productPopupPdpLoading.hidden = !isLoading;
      }
    }

    clearPdpSections() {
      if (this.dom.productPopupCollapsibles) {
        this.dom.productPopupCollapsibles.innerHTML = '';
      }
      if (this.dom.productPopupPdpSections) {
        this.dom.productPopupPdpSections.innerHTML = '';
      }
      this.setPdpSectionsLoading(false);
    }

    isMobilePopupView() {
      return window.matchMedia('(max-width: 767px)').matches;
    }

    destroyPopupSlider() {
      if (this.popupSwiper) {
        this.popupSwiper.destroy(true, true);
        this.popupSwiper = null;
      }
    }

    initPopupMobileSlider(container) {
      const SwiperClass = window.MinimogLibs?.Swiper || window.Swiper;
      if (!SwiperClass || !container) return;

      this.popupSwiper = new SwiperClass(container, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        initialSlide: this.popupActiveImageIndex,
        threshold: 5,
        pagination: {
          el: container.querySelector('.swiper-pagination'),
          clickable: true,
        },
      });
    }

    getPopupImages(product) {
      if (product.images?.length) return product.images;
      return product.featuredImage ? [product.featuredImage] : [];
    }

    renderProductPopup() {
      const product = this.activePopupProduct;
      if (!product) return;

      const images = this.getPopupImages(product);

      if (this.dom.productPopupTitle) {
        this.dom.productPopupTitle.textContent = product.title;
      }

      this.renderProductPopupGallery(images);
      this.updateProductPopupPrices(product);
      this.updateProductPopupAddButtons(product);
    }

    renderProductPopupGallery(images) {
      if (!this.dom.productPopupMainImage) return;

      this.destroyPopupSlider();

      const title = this.activePopupProduct?.title || '';
      const isMobile = this.isMobilePopupView();

      if (isMobile && images.length > 1) {
        this.dom.productPopupMainImage.className =
          'bundle-builder__product-popup-main bundle-builder__product-popup-slider swiper';
        this.dom.productPopupMainImage.innerHTML = `
          <div class="swiper-wrapper">
            ${images
              .map(
                (image) => `
              <div class="swiper-slide">
                <img src="${image}" alt="${title}" loading="lazy">
              </div>
            `
              )
              .join('')}
          </div>
          <div class="swiper-pagination"></div>
        `;
        this.initPopupMobileSlider(this.dom.productPopupMainImage);
      } else {
        this.dom.productPopupMainImage.className = 'bundle-builder__product-popup-main';
        const activeImage = images[this.popupActiveImageIndex] || images[0] || '';
        this.dom.productPopupMainImage.innerHTML = activeImage
          ? `<img src="${activeImage}" alt="${title}" loading="lazy">`
          : '';
      }

      if (!this.dom.productPopupThumbs) return;

      if (isMobile || images.length <= 1) {
        this.dom.productPopupThumbs.innerHTML = '';
        this.dom.productPopupThumbs.hidden = true;
        return;
      }

      this.dom.productPopupThumbs.hidden = false;
      this.dom.productPopupThumbs.innerHTML = images
        .map(
          (image, index) => `
            <button
              type="button"
              class="bundle-builder__product-popup-thumb${index === this.popupActiveImageIndex ? ' is-active' : ''}"
              data-product-popup-thumb
              data-index="${index}"
              aria-label="View image ${index + 1}"
            >
              <img src="${image}" alt="" loading="lazy">
            </button>
          `
        )
        .join('');
    }

    setPopupImage(index) {
      const images = this.getPopupImages(this.activePopupProduct || {});
      if (!images.length || index < 0 || index >= images.length) return;
      this.popupActiveImageIndex = index;
      this.renderProductPopupGallery(images);
    }

    buildPopupPriceHtml(product) {
      const compareHtml =
        product.compareAtPrice > product.price
          ? `<span class="bundle-builder__product-popup-compare">${this.formatMoney(product.compareAtPrice)}</span>`
          : '';

      return `${compareHtml}<span class="bundle-builder__product-popup-price">${this.formatMoney(product.price)}</span>`;
    }

    updateProductPopupPrices(product) {
      const priceHtml = this.buildPopupPriceHtml(product);
      if (this.dom.productPopupPrices) {
        this.dom.productPopupPrices.innerHTML = priceHtml;
      }
      if (this.dom.productPopupMobilePrices) {
        this.dom.productPopupMobilePrices.innerHTML = priceHtml;
      }
    }

    updateProductPopupAddButtons(product) {
      const atLimit = this.isAtLimit() && !this.getProductQty(product.id);
      const disabled = !product.available || atLimit;
      const label = !product.available
        ? this.config.soldOutText || 'Sold out'
        : this.config.addToBoxText || 'Add to Box';

      [this.dom.productPopupAdd, this.dom.productPopupMobileAdd].forEach((btn) => {
        if (!btn) return;
        btn.disabled = disabled;
        btn.textContent = label;
      });
    }

    initProductPopupRating() {
      const product = this.activePopupProduct;
      if (!product || !this.dom.productPopupRating) return;

      this.dom.productPopupRating.innerHTML = `
        <div class="jdgm-widget jdgm-preview-badge" data-id="${product.id}" data-auto-install="false">
          ${product.ratingHtml || ''}
        </div>
      `;

      if (typeof window.jdgm !== 'undefined' && typeof window.jdgm.customizeBadges === 'function') {
        window.jdgm.customizeBadges();
      }

      waitForElement('.jdgm-prev-badge, .jdgm-preview-badge', this.dom.productPopupRating, 3000).catch(() => {});
    }

    handleProductPopupAdd() {
      const product = this.activePopupProduct;
      if (!product || !product.available) return;
      if (this.isAtLimit() && !this.getProductQty(product.id)) return;

      if (product.variants.length === 1) {
        this.addVariant(product.variants[0], product);
        this.closeProductPopup();
        return;
      }

      this.closeProductPopup();
      this.openVariantModal(product);
    }

    handleAddClick(btn) {
      const productId = btn.dataset.productId;
      const product = this.getProducts().find((p) => String(p.id) === String(productId));
      if (!product || !product.available) return;
      if (this.isAtLimit()) return;

      if (product.variants.length === 1) {
        this.addVariant(product.variants[0], product);
      } else {
        this.openVariantModal(product);
      }
    }

    openVariantModal(product) {
      this.activeProduct = product;
      this.selectedOptions = {};

      product.options.forEach((option, index) => {
        this.selectedOptions[`option${index + 1}`] = option.values[0];
      });

      this.dom.modalTitle.textContent = product.title;
      this.dom.modalImage.innerHTML = product.featuredImage
        ? `<img src="${product.featuredImage}" alt="${product.title}" loading="lazy">`
        : '';

      this.renderModalOptions();
      this.updateModalVariant();
      this.dom.modal?.classList.add('is-open');
      this.lockBodyScroll();
    }

    closeModal() {
      this.dom.modal?.classList.remove('is-open');
      this.unlockBodyScroll();
      this.activeProduct = null;
    }

    renderModalOptions() {
      if (!this.activeProduct || !this.dom.modalOptions) return;

      this.dom.modalOptions.innerHTML = this.activeProduct.options
        .map((option, index) => {
          const optionKey = `option${index + 1}`;
          const optionsHtml = option.values
            .map(
              (value) =>
                `<option value="${value}" ${this.selectedOptions[optionKey] === value ? 'selected' : ''}>${value}</option>`
            )
            .join('');

          return `
            <div class="bundle-builder__variant-option">
              <label for="bb-option-${index}">${option.name}</label>
              <select id="bb-option-${index}" data-option-key="${optionKey}">
                ${optionsHtml}
              </select>
            </div>
          `;
        })
        .join('');

      this.dom.modalOptions.querySelectorAll('select').forEach((select) => {
        select.addEventListener('change', (e) => {
          this.selectedOptions[e.target.dataset.optionKey] = e.target.value;
          this.updateModalVariant();
        });
      });
    }

    findSelectedVariant() {
      if (!this.activeProduct) return null;

      return this.activeProduct.variants.find((variant) => {
        return this.activeProduct.options.every((_, index) => {
          const key = `option${index + 1}`;
          return variant[key] === this.selectedOptions[key];
        });
      });
    }

    updateModalVariant() {
      const variant = this.findSelectedVariant();
      if (!variant) {
        this.dom.modalPrice.textContent = '';
        this.dom.modalAdd.disabled = true;
        return;
      }

      this.dom.modalPrice.textContent = this.formatMoney(variant.price);
      const atLimit = this.isAtLimit() && !this.getProductQty(this.activeProduct.id);
      this.dom.modalAdd.disabled = !variant.available || atLimit;

      if (!variant.available) {
        this.dom.modalAdd.textContent = this.config.soldOutText || 'Sold out';
      } else {
        this.dom.modalAdd.textContent = this.config.addToBoxText || 'Add to Box';
      }
    }

    confirmVariantAdd() {
      const variant = this.findSelectedVariant();
      if (!variant || !variant.available || !this.activeProduct) return;

      this.addVariant(variant, this.activeProduct);
      this.closeModal();
    }

    addVariant(variant, product) {
      const key = String(product.id);
      if (this.isAtLimit()) return;

      if (this.state[key]) {
        this.state[key].qty += 1;
        this.state[key].variantId = variant.id;
        this.state[key].price = variant.price;
        this.state[key].compareAt = variant.compare_at_price || variant.price;
        this.state[key].variantTitle = variant.title !== 'Default Title' ? variant.title : '';
        this.state[key].image = variant.featured_image?.src || product.featuredImage || '';
      } else {
        this.state[key] = {
          variantId: variant.id,
          productId: product.id,
          title: product.title,
          variantTitle: variant.title !== 'Default Title' ? variant.title : '',
          price: variant.price,
          compareAt: variant.compare_at_price || variant.price,
          image: variant.featured_image?.src || product.featuredImage || '',
          qty: 1,
        };
      }

      this.render();
    }

    changeQty(productId, delta) {
      if (delta > 0 && this.isAtLimit()) return;

      const key = String(productId);
      const item = this.state[key];
      if (!item) return;

      const newQty = item.qty + delta;

      if (newQty <= 0) {
        delete this.state[key];
      } else {
        this.state[key].qty = newQty;
      }

      this.render();
    }

    removeItem(productId) {
      delete this.state[String(productId)];
      this.render();
    }

    resetBundle() {
      this.state = {};
      this.render();
    }

    render() {
      this.renderGrid();
      this.renderSidebar();
      this.renderMobileBar();

      if (this.activePopupProduct) {
        this.updateProductPopupPrices(this.activePopupProduct);
        this.updateProductPopupAddButtons(this.activePopupProduct);
      }
    }

    renderGrid() {
      if (!this.dom.grid) return;

      this.dom.grid.querySelectorAll('[data-product-card]').forEach((card) => {
        const productId = card.dataset.productId;
        const qty = this.getProductQty(productId);
        const actions = card.querySelector('[data-card-actions]');
        const atLimit = this.isAtLimit();

        if (!actions) return;

        if (qty === 0) {
          actions.innerHTML = `
            <button type="button" class="bundle-builder__btn-add" data-add-product data-product-id="${productId}"${atLimit ? ' disabled' : ''}>
              ${this.config.addToBoxText || 'Add to Box'}
            </button>
          `;
        } else {
          actions.innerHTML = `
            <div class="bundle-builder__qty-control">
              <button type="button" class="bundle-builder__qty-btn" data-decrease data-product-id="${productId}" aria-label="Decrease">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
              </button>
              <span class="bundle-builder__qty-label">${qty} ADDED</span>
              <button type="button" class="bundle-builder__qty-btn" data-increase data-product-id="${productId}" aria-label="Increase"${atLimit ? ' disabled' : ''}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
              </button>
            </div>
          `;
        }
      });

      const totals = this.getTotals();
      if (this.dom.selectedCount) {
        this.dom.selectedCount.innerHTML = `${totals.qty}<span>/${this.getRequiredCount()}</span>`;
      }
    }

    renderSidebar() {
      const totals = this.getTotals();
      const displayTotals = this.getDisplayTotals();
      const required = this.getRequiredCount();
      const progress = Math.min(100, (totals.qty / required) * 100);
      const items = Object.values(this.state);

      if (this.dom.sidebarStatus?.length) {
        const statusMessage = this.getStatusMessage(totals.qty, required);
        this.dom.sidebarStatus.forEach((node) => {
          node.textContent = statusMessage;
        });
      }

      if (this.dom.sidebarProgress) {
        this.dom.sidebarProgress.style.width = `${progress}%`;
      }

      if (this.dom.sidebarSlots) {
        this.dom.sidebarSlots.innerHTML = Array.from({ length: required }, (_, index) => {
          const filled = index < totals.qty;
          return `
            <div class="bundle-builder__slot${filled ? ' is-filled' : ''}">
              ${filled ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>' : index + 1}
            </div>
          `;
        }).join('');
      }

      if (this.dom.sidebarItems) {
        if (!items.length) {
          this.dom.sidebarItems.innerHTML = `
            <div class="bundle-builder__empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 10a4 4 0 0 1-8 0"/><path d="M3.103 6.034h17.794"/><path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z"/></svg>
              ${this.config.emptyText || 'Your bundle is empty.'}<br>${this.config.emptySubtext || 'Start adding products!'}
            </div>
          `;
        } else {
          this.dom.sidebarItems.innerHTML = `
            <ul class="bundle-builder__item-list">
              ${items
                .map(
                  (item) => `
                <li class="bundle-builder__item">
                  <div class="bundle-builder__item-thumb">
                    ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
                  </div>
                  <div class="bundle-builder__item-info">
                    <p class="bundle-builder__item-title">${item.title}</p>
                    <p class="bundle-builder__item-meta">Qty ${item.qty} · ${this.formatMoney(item.price * item.qty)}</p>
                  </div>
                  <button type="button" class="bundle-builder__item-remove" data-remove-item data-product-id="${item.productId}" aria-label="Remove one">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13.8124 1.73032H11.5056V1.40097C11.5056 0.628462 10.8771 0 10.1046 0H5.89529C5.12278 0 4.49432 0.628462 4.49432 1.40097V1.73032H2.18747C1.58319 1.73032 1.09131 2.22202 1.09131 2.8263V3.34593C1.09131 3.91162 1.5222 4.37843 2.07277 4.43605L3.20148 14.3406C3.30906 15.2865 4.10842 15.9998 5.06054 15.9998H10.9392C11.8913 15.9998 12.6906 15.2865 12.7982 14.3406L13.9269 4.43605C14.4777 4.3786 14.9084 3.91162 14.9084 3.34593V2.8263C14.9084 2.22202 14.4167 1.73032 13.8124 1.73032ZM5.20583 1.40097C5.20583 1.02058 5.51508 0.711332 5.89547 0.711332H10.1048C10.4852 0.711332 10.7944 1.02058 10.7944 1.40097V1.73032H5.20583V1.40097ZM12.0917 14.2601C12.025 14.8464 11.5296 15.2885 10.9395 15.2885H5.0609C4.47085 15.2885 3.9754 14.8464 3.90872 14.2601L2.78997 4.44209H13.2106L12.0917 14.2601ZM14.1974 3.34593C14.1974 3.55808 14.0247 3.73076 13.8126 3.73076H2.18747C1.97532 3.73076 1.80264 3.55808 1.80264 3.34593V2.8263C1.80264 2.61432 1.97532 2.44165 2.18747 2.44165H13.8122C14.0244 2.44165 14.1971 2.61432 14.1971 2.8263V3.34593H14.1974Z" fill="#94C2CC"/>
                      <path d="M7.99995 13.3635C8.19628 13.3635 8.35562 13.2042 8.35562 13.0078V6.72285C8.35562 6.52653 8.19628 6.36719 7.99995 6.36719C7.80363 6.36719 7.64429 6.52653 7.64429 6.72285V13.0078C7.64429 13.2042 7.80363 13.3635 7.99995 13.3635Z" fill="#94C2CC"/>
                      <path d="M10.1261 13.3469C10.1378 13.3481 10.1495 13.3487 10.1609 13.3487C10.342 13.3487 10.4967 13.2114 10.5145 13.0275L11.1237 6.77203C11.1427 6.57641 10.9998 6.40249 10.8043 6.38346C10.606 6.36337 10.4348 6.50741 10.4158 6.70285L9.80651 12.9583C9.78748 13.1539 9.93064 13.3278 10.1261 13.3469Z" fill="#94C2CC"/>
                      <path d="M5.83893 13.3486C5.85031 13.3486 5.86205 13.3481 5.87379 13.3468C6.06923 13.328 6.2122 13.1539 6.19318 12.9583L5.58392 6.70282C5.56489 6.50738 5.39364 6.36352 5.19536 6.38343C4.99992 6.40228 4.85694 6.57638 4.87597 6.772L5.48522 13.0275C5.50318 13.2112 5.65808 13.3486 5.83893 13.3486Z" fill="#94C2CC"/>
                    </svg>
                  </button>
                </li>
              `
                )
                .join('')}
            </ul>
          `;
        }
      }

      if (this.dom.sidebarItemCount) {
        this.dom.sidebarItemCount.textContent = totals.qty;
      }

      if (this.dom.sidebarSavingsRow) {
        this.dom.sidebarSavingsRow.hidden = displayTotals.savings <= 0;
      }

      if (this.dom.sidebarSavings) {
        this.dom.sidebarSavings.textContent =
          displayTotals.savings > 0 ? this.formatMoney(displayTotals.compare) : '';
      }

      if (this.dom.sidebarCompare) {
        this.dom.sidebarCompare.hidden = true;
        this.dom.sidebarCompare.textContent = '';
      }

      if (this.dom.sidebarSubtotal) {
        this.dom.sidebarSubtotal.textContent = this.formatMoney(displayTotals.price);
      }

      if (this.dom.sidebarFreeItems) {
        this.dom.sidebarFreeItems.hidden = !this.isBundleComplete();
      }

      const checkoutText = this.config.checkoutText || 'Add to Cart';
      [this.dom.sidebarCheckout, this.dom.mobileCheckout].forEach((btn) => {
        if (!btn) return;
        if (!btn.classList.contains('is-loading')) {
          btn.disabled = !this.isBundleComplete();
        }
        const label = btn.querySelector('.bundle-builder__btn-label');
        if (label) {
          label.textContent = checkoutText;
        }
      });
    }

    setCheckoutLoading(isLoading) {
      [this.dom.sidebarCheckout, this.dom.mobileCheckout].forEach((btn) => {
        if (!btn) return;
        btn.classList.toggle('is-loading', isLoading);
        btn.disabled = isLoading || !this.isBundleComplete();
        btn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      });
    }

    renderMobileBar() {
      const totals = this.getTotals();
      const displayTotals = this.getDisplayTotals();
      const required = this.getRequiredCount();
      const progress = Math.min(100, (totals.qty / required) * 100);

      if (this.dom.mobileStatus) {
        this.dom.mobileStatus.textContent = this.getStatusMessage(totals.qty, required);
      }

      if (this.dom.mobileBundleStatus) {
        this.dom.mobileBundleStatus.hidden = totals.qty > 0;
      }

      if (this.dom.mobileActive) {
        this.dom.mobileActive.hidden = totals.qty === 0;
      }

      if (this.dom.mobileSlots) {
        this.dom.mobileSlots.innerHTML = Array.from({ length: required }, (_, index) => {
          const filled = index < totals.qty;
          const slotMarkup = filled
            ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="M12 5v14"/></svg>';
          const separator = index === 0 ? '<span class="bundle-builder__mobile-slot-separator">+</span>' : '';

          return `
            <div class="bundle-builder__mobile-slot${filled ? ' is-filled' : ''}">
              ${slotMarkup}
            </div>
            ${separator}
          `;
        }).join('');
      }

      if (this.dom.mobileProgress) {
        this.dom.mobileProgress.style.width = `${progress}%`;
      }

      if (this.dom.mobileCount) {
        this.dom.mobileCount.textContent = `${totals.qty}/${required} SELECTED`;
      }

      if (this.dom.mobilePrice) {
        this.dom.mobilePrice.textContent = this.formatMoney(displayTotals.price);
      }

      if (this.dom.mobileCompare) {
        if (displayTotals.savings > 0) {
          this.dom.mobileCompare.hidden = false;
          this.dom.mobileCompare.textContent = this.formatMoney(displayTotals.compare);
        } else {
          this.dom.mobileCompare.hidden = true;
        }
      }

      if (this.dom.mobileFreeItems) {
        this.dom.mobileFreeItems.hidden = !this.isBundleComplete();
      }

      if (this.dom.mobileThumbs) {
        const units = this.getUnitItems();
        const parts = [];

        for (let index = 0; index < required; index += 1) {
          if (index === 1) {
            parts.push('<span class="bundle-builder__mobile-thumb-separator">+</span>');
          }

          const item = units[index];

          if (item) {
            parts.push(`
              <div class="bundle-builder__mobile-thumb">
                <div class="bundle-builder__mobile-thumb-img">
                  ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
                </div>
                <button type="button" class="bundle-builder__mobile-thumb-remove" data-remove-item data-product-id="${item.productId}" aria-label="Remove ${item.title}">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
              </div>
            `);
          } else {
            parts.push(`
              <div class="bundle-builder__mobile-slot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
              </div>
            `);
          }
        }

        this.dom.mobileThumbs.innerHTML = parts.join('');
      }
    }

    async handleCheckout(button) {
      if (!this.isBundleComplete()) return;

      const items = Object.values(this.state).map((item) => ({
        id: item.variantId,
        quantity: item.qty,
      }));

      if (!items.length) return;

      this.setCheckoutLoading(true);
      if (this.dom.error) this.dom.error.textContent = '';

      const cartDrawer = document.querySelector('m-cart-drawer');
      let data = { items };

      if (cartDrawer) {
        data = {
          ...data,
          sections: cartDrawer.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
      }

      const config = fetchConfig('javascript');
      config.method = 'POST';
      config.body = JSON.stringify(data);

      try {
        const response = await fetch(`${window.MinimogSettings.routes.cart_add_url}`, config);
        const result = await response.json();

        if (result.status) {
          this.showError(result.description || 'Failed to add items to cart.');
          return;
        }

        this.handleCartSuccess(result, cartDrawer);
        this.resetBundle();
      } catch (err) {
        console.error('Bundle checkout error:', err);
        this.showError('An error occurred while adding items to the cart.');
      } finally {
        this.setCheckoutLoading(false);
      }
    }

    handleCartSuccess(response, cartDrawer) {
      const { MinimogSettings, MinimogStrings } = window;

      if (MinimogSettings.use_ajax_atc) {
        if (!MinimogSettings.enable_cart_drawer) {
          window.MinimogTheme.Notification.show({
            target: this.dom.error || document.body,
            method: 'appendChild',
            type: 'success',
            message: MinimogStrings.itemAdded,
            last: 3000,
          });
        } else if (cartDrawer) {
          cartDrawer.renderContents(response);
          MinimogTheme.Notification.show({
            target: cartDrawer.querySelector('m-cart-drawer-items'),
            method: 'prepend',
            type: 'success',
            message: MinimogStrings.itemAdded,
            delay: 400,
          });
        }

        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: response } }));
        window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, response);
      } else {
        window.location = MinimogSettings.routes.cart;
      }
    }

    showError(message) {
      if (this.dom.error) {
        this.dom.error.textContent = message;
      }

      if (window.MinimogTheme?.Notification) {
        MinimogTheme.Notification.show({
          target: this.dom.error || document.body,
          method: 'appendChild',
          type: 'warning',
          message,
        });
      }
    }

    formatMoney(cents) {
      if (typeof formatMoney === 'function' && window.MinimogSettings?.money_format) {
        return formatMoney(cents, MinimogSettings.money_format);
      }
      return (cents / 100).toFixed(2);
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
